import React from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import { useMaterialUIController } from "context";

function DetallesCliente({ open, onClose, cliente }) {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  if (!cliente) return null;

  const renderDetailItem = (label, value) => (
    <MDBox mb={1} display="flex" flexDirection="column">
      <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
        {label}
      </MDTypography>
      <MDTypography variant="body2" color="dark">
        {value || "N/A"}
      </MDTypography>
    </MDBox>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 0,
        }}
      >
        {/* Header */}
        <MDBox
          bgColor={sidenavColor}
          borderRadius="8px 8px 0 0"
          p={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDTypography variant="h5" color="white" fontWeight="medium">
            Detalles del Cliente: {cliente.nombre}
          </MDTypography>
          <MDBadge
            badgeContent={cliente.estatus === "activo" ? "Activo" : "Inactivo"}
            color={cliente.estatus === "activo" ? "success" : "dark"}
            variant="gradient"
            size="sm"
          />
        </MDBox>

        <MDBox p={3}>
          {/* Datos Básicos */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Datos Básicos</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MDBox display="flex" flexWrap="wrap" gap={3}>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Nombre o Razón Social", cliente.nombre)}
                </MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Documento", `${cliente.tipo_documento}: ${cliente.rif}`)}
                </MDBox>
                <MDBox flex="1 1 45%">{renderDetailItem("Email", cliente.email)}</MDBox>
                <MDBox flex="1 1 45%">{renderDetailItem("Teléfono", cliente.telefono)}</MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Tipo de Cliente", cliente.tipo_cliente)}
                </MDBox>
                <MDBox flex="1 1 45%">{renderDetailItem("Empresa", cliente.empresa?.nombre)}</MDBox>
              </MDBox>
            </AccordionDetails>
          </Accordion>

          {/* Dirección */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Dirección</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MDBox display="flex" flexWrap="wrap" gap={3}>
                <MDBox flex="1 1 100%">{renderDetailItem("Dirección", cliente.direccion)}</MDBox>
                <MDBox flex="1 1 30%">{renderDetailItem("País", cliente.pais)}</MDBox>
                <MDBox flex="1 1 30%">{renderDetailItem("Ciudad/Provincia", cliente.ciudad)}</MDBox>
                <MDBox flex="1 1 30%">
                  {renderDetailItem("Código Postal", cliente.codigo_postal)}
                </MDBox>
              </MDBox>
            </AccordionDetails>
          </Accordion>

          {/* Datos Comerciales */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Datos Comerciales</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MDBox display="flex" flexWrap="wrap" gap={3}>
                <MDBox flex="1 1 45%">{renderDetailItem("Moneda", cliente.moneda)}</MDBox>
                <MDBox flex="1 1 45%">{renderDetailItem("Tipo", cliente.tipo)}</MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Lista de Precios", cliente.lista_precios)}
                </MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Descuento (%)", `${cliente.descuento_defecto}%`)}
                </MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Condición de Pago", cliente.condicion_pago)}
                </MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Límite de Crédito", cliente.limite_credito)}
                </MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Vendedor Asignado", cliente.vendedor?.nombre)}
                </MDBox>
              </MDBox>
            </AccordionDetails>
          </Accordion>

          {/* Facturación */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Facturación</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MDBox display="flex" flexWrap="wrap" gap={3}>
                <MDBox flex="1 1 100%">
                  {renderDetailItem("Nombre Fiscal", cliente.nombre_fiscal)}
                </MDBox>
                <MDBox flex="1 1 100%">
                  {renderDetailItem("Dirección Fiscal", cliente.direccion_fiscal)}
                </MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Identificación Fiscal", cliente.identificacion_fiscal)}
                </MDBox>
                <MDBox flex="1 1 45%">
                  {renderDetailItem("Email Facturación", cliente.email_facturacion)}
                </MDBox>
              </MDBox>
            </AccordionDetails>
          </Accordion>

          {/* Contactos */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Contactos</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {cliente.contactos && cliente.contactos.length > 0 ? (
                cliente.contactos.map((contacto, index) => (
                  <MDBox key={index} mb={2} p={2} border="1px solid #eee" borderRadius="4px">
                    <MDBox display="flex" justifyContent="space-between">
                      <MDTypography variant="h6" color="dark">
                        {contacto.nombre}
                      </MDTypography>
                      {contacto.es_principal && (
                        <MDBadge badgeContent="Principal" color="info" size="xs" container />
                      )}
                    </MDBox>
                    <MDBox display="flex" flexWrap="wrap" gap={2} mt={1}>
                      <MDBox flex="1 1 30%">{renderDetailItem("Cargo", contacto.cargo)}</MDBox>
                      <MDBox flex="1 1 30%">{renderDetailItem("Email", contacto.email)}</MDBox>
                      <MDBox flex="1 1 30%">
                        {renderDetailItem("Teléfono", contacto.telefono)}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                ))
              ) : (
                <MDTypography variant="body2" color="text">
                  No hay contactos registrados.
                </MDTypography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Extras */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Extras</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MDBox mb={2}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text"
                  textTransform="uppercase"
                >
                  Etiquetas
                </MDTypography>
                <MDBox display="flex" gap={1} flexWrap="wrap" mt={1}>
                  {cliente.etiquetas && cliente.etiquetas.length > 0 ? (
                    cliente.etiquetas.map((tag, index) => (
                      <Chip
                        key={index}
                        label={typeof tag === "string" ? tag : tag.nombre}
                        color="primary"
                        size="small"
                      />
                    ))
                  ) : (
                    <MDTypography variant="body2" color="text">
                      Sin etiquetas.
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
              <Divider />
              <MDBox mt={2}>{renderDetailItem("Notas Internas", cliente.notas_internas)}</MDBox>
            </AccordionDetails>
          </Accordion>
        </MDBox>

        {/* Footer */}
        <MDBox p={3} display="flex" justifyContent="flex-end" borderTop="1px solid #ddd">
          <MDButton variant="gradient" color={sidenavColor} onClick={onClose}>
            Cerrar
          </MDButton>
        </MDBox>
      </Box>
    </Modal>
  );
}

DetallesCliente.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cliente: PropTypes.object,
};

export default DetallesCliente;
